import { useMemo } from 'react';
import { useAppSelector, useAppDispatch } from '../../app/hooks';
import {
  SmallDeviceHowToEditReportingSteps,
  SmallDeviceHowToAccessLeoSteps,
  SmallDeviceHowToAccessTerminalModeSteps,
  LargeDeviceHowToEditReportingSteps,
  LargeDeviceHowToAccessLeoSteps,
  LargeDeviceHowToAccessTerminalModeSteps,
  HowToStep,
  SmallDeviceViewPortsFromLeo,
  LargeDeviceViewPortsFromLeo,
  LargeDeviceViewPortsFromController,
  SmallDeviceViewPortsFromController,
  SmallDeviceViewDropDownFromLeo,
  LargeDeviceViewDropDownFromLeo,
  LargeDeviceViewDropDownFromController,
  SmallDeviceViewDropDownFromController,
} from '../../components/Modals/Constants';
import {
  selectShowHowToAccessLeoDialog,
  selectShowHowToAccessTerminalModeDialog,
  selectShowHowToEditReportingDialog,
  handleHideHowToAccessLeoDialog,
  handleHideHowToAccessTerminalModeDialog,
  handleHideHowToEditReportingDialog,
} from '../../features/view/viewSlice';

export type DialogKey = 'editReporting' | 'accessLeo' | 'accessController';

type DialogConfig = {
  key: DialogKey;
  title: string;
  open: boolean;
  handleClose: () => void;
  howToSteps: HowToStep[];
};

function appendSteps(
  key: DialogKey,
  stepsMap: Record<DialogKey, Array<HowToStep>>,
  stepsToAppend: Array<HowToStep> | Array<Array<HowToStep>>,
) {
  const flattenedSteps = Array.isArray(stepsToAppend[0])
    ? (stepsToAppend as Array<Array<HowToStep>>).flat()
    : (stepsToAppend as Array<HowToStep>);
  stepsMap[key] = [...(stepsMap[key] ?? []), ...flattenedSteps];
}

export const useHowToAccessLeoAndControllerDialogs = (
  mobileScreen: boolean,
  canRemoteAccessLeo: boolean,
  canRemoteAccessController: boolean,
  canViewRemoteAccessPort: boolean,
): Array<DialogConfig> => {
  const dispatch = useAppDispatch();

  const showHowToEditReportingPrefs = useAppSelector(
    selectShowHowToEditReportingDialog,
  );
  const showHowToAccessLeo = useAppSelector(selectShowHowToAccessLeoDialog);
  const showHowToAccesTerminalMode = useAppSelector(
    selectShowHowToAccessTerminalModeDialog,
  );

  return useMemo((): Array<DialogConfig> => {
    const smallDeviceStepsMap: Record<DialogKey, HowToStep[]> = {
      editReporting: SmallDeviceHowToEditReportingSteps,
      accessLeo: SmallDeviceHowToAccessLeoSteps,
      accessController: SmallDeviceHowToAccessTerminalModeSteps,
    };

    const largeDeviceStepsMap: Record<DialogKey, HowToStep[]> = {
      editReporting: LargeDeviceHowToEditReportingSteps,
      accessLeo: LargeDeviceHowToAccessLeoSteps,
      accessController: LargeDeviceHowToAccessTerminalModeSteps,
    };

    if (canRemoteAccessLeo && canRemoteAccessController) {
      // append dropdown step gifs to controller tutorial
      appendSteps(
        'accessController',
        smallDeviceStepsMap,
        SmallDeviceViewDropDownFromController,
      );
      appendSteps(
        'accessController',
        largeDeviceStepsMap,
        LargeDeviceViewDropDownFromController,
      );
      // append dropdown step gifs to leo tutorial
      appendSteps(
        'accessLeo',
        smallDeviceStepsMap,
        SmallDeviceViewDropDownFromLeo,
      );
      appendSteps(
        'accessLeo',
        largeDeviceStepsMap,
        LargeDeviceViewDropDownFromLeo,
      );

      if (canViewRemoteAccessPort) {
        // append port steps gifs from accessing leo
        appendSteps(
          'accessLeo',
          smallDeviceStepsMap,
          SmallDeviceViewPortsFromLeo,
        );
        appendSteps(
          'accessLeo',
          largeDeviceStepsMap,
          LargeDeviceViewPortsFromLeo,
        );

        // append port steps gifs from accessssing controller
        appendSteps(
          'accessController',
          smallDeviceStepsMap,
          SmallDeviceViewPortsFromController,
        );
        appendSteps(
          'accessController',
          largeDeviceStepsMap,
          LargeDeviceViewPortsFromController,
        );
      }
    } else if (!canRemoteAccessLeo && canRemoteAccessController) {
      // append dropdown step gifs to controller tutorial
      appendSteps(
        'accessController',
        smallDeviceStepsMap,
        SmallDeviceViewDropDownFromController,
      );
      appendSteps(
        'accessController',
        largeDeviceStepsMap,
        LargeDeviceViewDropDownFromController,
      );
      if (canViewRemoteAccessPort) {
        // append port steps gifs from accessssing controller
        appendSteps(
          'accessController',
          smallDeviceStepsMap,
          SmallDeviceViewPortsFromController,
        );
        appendSteps(
          'accessController',
          largeDeviceStepsMap,
          LargeDeviceViewPortsFromController,
        );
      }
    }

    const stepsMap = mobileScreen ? smallDeviceStepsMap : largeDeviceStepsMap;

    return [
      {
        key: 'editReporting',
        title: 'How To Update Email Notifications',
        open: showHowToEditReportingPrefs,
        handleClose: () => dispatch(handleHideHowToEditReportingDialog()),
        howToSteps: stepsMap.editReporting,
      },
      {
        key: 'accessLeo',
        title: 'How to Connect to a LEO Facility Manager',
        open: showHowToAccessLeo,
        handleClose: () => dispatch(handleHideHowToAccessLeoDialog()),
        howToSteps: stepsMap.accessLeo,
      },
      {
        key: 'accessController',
        title: 'How to Connect to a Controller',
        open: showHowToAccesTerminalMode,
        handleClose: () => dispatch(handleHideHowToAccessTerminalModeDialog()),
        howToSteps: stepsMap.accessController,
      },
    ];
  }, [
    canRemoteAccessLeo,
    canRemoteAccessController,
    canViewRemoteAccessPort,
    mobileScreen,
    showHowToEditReportingPrefs,
    showHowToAccessLeo,
    showHowToAccesTerminalMode,
    dispatch,
  ]);
};
